/**
 * Admin link and API URL builders.
 *
 * Assessment ids are derived from the client's last name, which is free text,
 * so they can contain characters that are not safe in a URL ("+", "&", ",",
 * "/", "#", "?", "%", spaces...). Interpolating them raw breaks in two ways:
 *
 * - in a query string, "+" is decoded back as a space by any form decoder
 *   (URLSearchParams included), so the id no longer matches the stored one;
 * - in a path segment, "/" adds a segment, "#" and "?" truncate the URL and a
 *   lone "%" makes the server fail to decode the parameter.
 *
 * Every admin link therefore goes through these helpers, which leave the
 * encoding to URLSearchParams / encodeURIComponent.
 */

// ── Comparison links ────────────────────────────────────────
// One `ids` entry per assessment, so no separator can clash with an id.

/**
 * Build the query parameters of a comparison link.
 * @param {string[]} ids - Assessment ids, in display order
 * @returns {URLSearchParams}
 */
export const buildCompareSearchParams = (ids) => {
  const params = new URLSearchParams()
  ids.forEach(id => params.append('ids', id))
  return params
}

/**
 * Build the full in-app path of a comparison link.
 * @param {string[]} ids - Assessment ids, in display order
 * @returns {string} - e.g. "/admin/compare?ids=CEDEC-1-A&ids=CEDEC-2-B"
 */
export const buildComparePath = (ids) =>
  `/admin/compare?${buildCompareSearchParams(ids).toString()}`

/**
 * Read the assessment ids out of a comparison link.
 * Accepts both the current format (one `ids` entry per assessment) and the
 * legacy comma-separated format, so older bookmarks keep working.
 * @param {URLSearchParams} searchParams - Query parameters of the current URL
 * @returns {string[]} - Assessment ids, possibly empty
 */
export const parseCompareIds = (searchParams) => {
  const entries = searchParams.getAll('ids')
  const ids = entries.length > 1 ? entries : (entries[0] || '').split(',')
  return ids.map(id => id.trim()).filter(id => id)
}

// ── Assessment links ────────────────────────────────────────

/**
 * Build the in-app path of an assessment detail page.
 * @param {string} id - Assessment id
 * @returns {string}
 */
export const assessmentDetailPath = (id) =>
  `/admin/assessments/${encodeURIComponent(id)}`

/**
 * Build an assessment API URL.
 * @param {string} id - Assessment id
 * @param {string} [action] - Optional sub-resource, e.g. 'archive'
 * @returns {string}
 */
export const assessmentApiUrl = (id, action = '') =>
  `/api/admin/assessments/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── User links ──────────────────────────────────────────────

/**
 * Build an admin user API URL.
 * @param {string} email - User email, used as identifier
 * @param {string} [action] - Optional sub-resource, e.g. 'role'
 * @returns {string}
 */
export const userApiUrl = (email, action = '') =>
  `/api/admin/users/${encodeURIComponent(email)}${action ? `/${action}` : ''}`

export default {
  buildCompareSearchParams,
  buildComparePath,
  parseCompareIds,
  assessmentDetailPath,
  assessmentApiUrl,
  userApiUrl
}
